import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import { format } from 'date-fns';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const CommitHistory = ({ repoName, branchName, accessToken }) => {
  const [commits, setCommits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCommits = async () => {
      if (!repoName || !branchName || !accessToken) {
        console.log('Missing required props:', { repoName, branchName, hasToken: !!accessToken });
        setError('Missing required information');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log(`Fetching commits for ${repoName}/${branchName}`);
        
        const response = await axios.get(
          `${BACKEND_URL}/api/repositories/${repoName}/branches/${branchName}/commits`,
          {
            headers: { 
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
          }
        );

        console.log('Received commits response:', response.data);
        
        if (!Array.isArray(response.data)) {
          console.error('Invalid response format:', response.data);
          setError('Invalid response format from server');
          setCommits([]);
        } else {
          setCommits(response.data);
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching commits:', err);
        console.error('Error response:', err.response?.data);
        const errorMessage = err.response?.data?.error || err.message || 'Failed to fetch commit history';
        setError(errorMessage);
        setCommits([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCommits();
  }, [repoName, branchName, accessToken]);

  if (loading) {
    return (
      <div className="text-center p-3">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading commit history...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error Loading Commits</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  if (!commits || commits.length === 0) {
    return (
      <Alert variant="info">
        No commit history available for this branch.
      </Alert>
    );
  }

  return (
    <Card>
      <Card.Header>
        <h5 className="mb-0">Commit History for {branchName}</h5>
      </Card.Header>
      <Card.Body>
        <Table responsive hover>
          <thead>
            <tr>
              <th>Message</th>
              <th>Author</th>
              <th>Date</th>
              <th>Changes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {commits.map((commit) => (
              <tr key={commit.sha}>
                <td style={{ maxWidth: '300px', whiteSpace: 'pre-wrap' }}>
                  {commit.message}
                </td>
                <td>
                  {commit.author?.name || 'Unknown'}
                  {commit.author?.email && (
                    <small className="text-muted d-block">{commit.author.email}</small>
                  )}
                </td>
                <td>
                  {commit.author?.date ? 
                    format(new Date(commit.author.date), 'MMM d, yyyy HH:mm') :
                    'Unknown date'
                  }
                </td>
                <td>
                  {commit.stats && (
                    <>
                      <Badge bg="success" className="me-1">+{commit.stats.additions || 0}</Badge>
                      <Badge bg="danger">-{commit.stats.deletions || 0}</Badge>
                    </>
                  )}
                </td>
                <td>
                  {commit.html_url && (
                    <a
                      href={commit.html_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-primary"
                    >
                      View on GitHub
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

export default CommitHistory;
